import { showSuccess } from '@calendly/alert'
import { I18n } from '@calendly/i18n'
import { Segment } from '@calendly/segment'

import { setGetParams } from 'actions/core/navigation'
import { getUser } from 'common'
import { buildSubmitFormPayload, closeModal, formDataSelector, transitionModal } from 'features/one_off_modal'
import { openOneOffShareModal } from 'features/one_off_share_modal'
import { rootApi } from 'root-api'
import { loadLocale } from 'services/load_locale'

import { createUrl } from '../shared/create_url'

const api = rootApi.enhanceEndpoints({ addTagTypes: ['MEETING-POLLS', 'MEETING-POLLS-PARTIAL-LIST'] }).injectEndpoints({
  endpoints: (build) => ({
    fetchPoll: build.query({
      query: (id) => ({
        url: `/api/scheduled_events/polls/${id}`,
      }),
      providesTags: (result) => [{ type: 'MEETING-POLLS', id: result?.id }],
    }),
    fetchPolls: build.query({
      query: ({ scopeId, scopeType, status, filterTerm, pageSize, page }) => ({
        url: createUrl('polls', scopeType, scopeId),
        params: {
          filterTerm,
          status,
          pageSize,
          page
        },
      }),
      providesTags: (result) => (result?.results
        ? [...result.results.map(({ id }) => ({ type: 'MEETING-POLLS', id })),
          { type: 'MEETING-POLLS', id: 'MEETING-POLLS-PARTIAL-LIST' }]
        : [{ type: 'MEETING-POLLS', id: 'MEETING-POLLS-PARTIAL-LIST' }]
      ),
      forceRefetch: ({ currentArg, previousArg }) => (
        currentArg?.scopeId !== previousArg?.scopeId
        || currentArg?.scopeType !== previousArg?.scopeType
        || currentArg?.filterTerm !== previousArg?.filterTerm
        || currentArg?.status !== previousArg?.status
      ),
    }),
    updatePoll: build.mutation({
      queryFn: (data, { getState }, _extraOptions, baseQuery) => {
        const state = getState()
        const {
          durationField,
          selectedSpots,
          timezone,
          selectedMembers,
          reserveTimes,
          votesVisible,
          notifyInvitees,
          bookingMethod
        } = formDataSelector(state)
        const eventType = { ...data.eventTypeFields, ...durationField }
        const payload = buildSubmitFormPayload({
          eventType,
          selectedSpots,
          timezone,
          selectedMembers,
          reserveTimes,
          votesVisible,
          notifyInvitees,
          bookingMethod
        })

        payload.selected_spots = payload.selected_spots.map(({ start, end, high_priority: highPriority }) => ({
          start: start.toISOString(),
          end: end.toISOString(),
          highPriority
        }))

        return baseQuery({
          url: `/api/scheduled_events/polls/${data.eventTypeId}`,
          method: 'PATCH',
          data: { ...payload }
        })
      },
      invalidatesTags: (result) => [
        { type: 'MEETING-POLLS', id: result.id },
        { type: 'MEETING-POLLS', id: 'MEETING-POLLS-PARTIAL-LIST' }
      ],
      extraOptions: {
        onSuccess: (_, { dispatch }) => {
          dispatch(closeModal())
          dispatch(showSuccess(I18n.t('publisher.event_types.one_off.creation_modal.poll_updated')))
        }
      }
    }),
    createPoll: build.mutation({
      queryFn: (data, { getState }, _extraOptions, baseQuery) => {
        if (data?.eventTypeFields?.inviteeLocale) { loadLocale(data.eventTypeFields.inviteeLocale) }

        const state = getState()
        const {
          durationField,
          selectedSpots,
          timezone,
          reserveTimes,
          votesVisible,
          bookingMethod
        } = formDataSelector(state)
        const eventType = { ...data.eventTypeFields, ...durationField }
        const payload = buildSubmitFormPayload({
          eventType,
          selectedSpots,
          timezone,
          reserveTimes,
          votesVisible,
          bookingMethod
        })

        payload.selected_spots = payload.selected_spots.map(({ start, end, high_priority: highPriority }) => ({
          start: start.toISOString(),
          end: end.toISOString(),
          highPriority
        }))

        return baseQuery({
          url: '/api/users/me/adhoc_event_types',
          method: 'POST',
          data: { ...payload }
        })
      },
      invalidatesTags: [
        'MEETING-POLLS',
        'MEETING-POLLS-PARTIAL-LIST'
      ],
      extraOptions: {
        onSuccess: (response, { dispatch, getState }) => {
          const state = getState()
          const currentUser = getUser.select()(state).data
          const {
            reserveTimes,
            selectedSpots,
            durationField,
            hostsField,
          } = state.oneOffModal.availabilityStep

          const eventType = {
            id: response.id,
            uuid: response.uuid,
            name: response.name,
            locale: response.locale,
            duration: response.duration,
            timezone: response.timezone,
            bookingUrl: response.bookingUrl,
            bookingMethod: response.bookingMethod,
            locationType: response.locationType,
            locationTypesCount: response.locationTypesCount,
            kindDescription: response.kindDescription,
            reserveTimes
          }

          const spots = selectedSpots.map((spot) => spot.start.format())
          dispatch(transitionModal())
          dispatch(openOneOffShareModal({ eventType, email: { selectedSpots: spots } }))
          dispatch(setGetParams({ create_new_meeting: null }))

          const eventData = {
            meeting_type: 'oneOffEventType',
            event_type: response.name, // should be One-off Meeting
            location: response.location,
            meeting_duration: durationField.duration,
            calendar_connected: currentUser.has_active_notifiable_calendar,
            team_members_included: hostsField.selectedMembers.length,
          }
          Segment.track('Event Type Created - Client', eventData)
        }
      },
      forceRefetch: ({ currentArg, previousArg }) => (
        currentArg?.scopeId !== previousArg?.scopeId
        || currentArg?.scopeType !== previousArg?.scopeType
        || currentArg?.queryParams !== previousArg?.queryParams
      ),
    }),
    deletePoll: build.mutation({
      query: (id) => ({
        url: `${createUrl('polls')}/${id}`, method: 'DELETE'
      }),
      extraOptions: {
        onSuccess: (_, { dispatch }) => {
          dispatch(showSuccess(I18n.t('publisher.scheduling.polls.flash.poll_deleted')))
        }
      },
      invalidatesTags: (result) => [
        { type: 'MEETING-POLLS', id: 'MEETING-POLLS-PARTIAL-LIST' },
        { type: 'MEETING-POLLS', id: result?.id }
      ],
    }),
    batchDeletePolls: build.mutation({
      query: ({ pollsIds }) => ({
        url: createUrl('polls'),
        method: 'DELETE',
        params: {
          ids: pollsIds
        }
      }),
      onQueryStarted: async ({ pollsIds, name }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled
          dispatch(
            showSuccess(I18n.t('publisher.scheduling.polls.flash.delete_success', { name, count: pollsIds.length }))
          )
        } catch (e) {
          // do nothing
        }
      },
      invalidatesTags: (_1, _2, { pollsIds }) => [
        ...pollsIds.map((id) => ({ type: 'MEETING-POLLS', id })),
        { type: 'MEETING-POLLS', id: 'MEETING-POLLS-PARTIAL-LIST' },
      ],
    })
  })
})

export const {
  useFetchPollQuery,
  useFetchPollsQuery,
  useCreatePollMutation,
  useUpdatePollMutation,
  useDeletePollMutation,
  useBatchDeletePollsMutation
} = api
